import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import Logo from "../../assets/img/Quantus-Invoice.png";
import LogoCollapse from "../../assets/img/Quantus-Invoice3.png";
import { AuthContext } from "../../context/AuthContext";
import { MENU_HELP, MenuHelpModal } from "./MenuHelpModal";

// Tooltip para desktop colapsado
function Tooltip({ targetRef, text, visible }) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (targetRef?.current && visible) {
      const rect = targetRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [targetRef, visible]);

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        transform: "translateY(-50%)",
        background: "linear-gradient(135deg,#1A1A1A 0%,#1C2267 100%)",
        color: "#fff",
        padding: "6px 11px",
        borderRadius: "8px",
        fontSize: "0.78rem",
        fontWeight: 500,
        fontFamily: "Kanit, sans-serif",
        letterSpacing: "0.01em",
        boxShadow: "0 8px 24px -6px rgba(13,13,16,0.55)",
        zIndex: 9999,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </div>,
    document.body
  );
}

export default function Sidebar({ collapsed, setCollapsed }) {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const rol = user?.rol?.toLowerCase().trim() || "";

  const [mobileOpen, setMobileOpen] = useState(false);

  const [tooltip, setTooltip] = useState({
    text: "",
    visible: false,
    ref: null,
  });

  // (2026-05-21) Ruta cuya ayuda contextual se está mostrando (o null).
  const [helpRuta, setHelpRuta] = useState(null);

  const isActive = (path) => location.pathname.includes(path);

  // (2026-05-18) Persistencia del scroll del menú lateral.
  // Cada página monta su propio AdminLayout→Sidebar, por lo que al navegar
  // el Sidebar se RE-MONTA y el contenedor scrolleable arrancaba en 0
  // (el menú "saltaba" al inicio). Se guarda la posición en sessionStorage
  // y se restaura al montar (useLayoutEffect = antes del paint, sin parpadeo).
  // La clave se limpia al iniciar/cerrar sesión (AuthContext), de modo que
  // "mostrarse desde el principio" solo aplica al ENTRAR a la plataforma.
  const SCROLL_KEY = "qi_sidebar_scroll";
  const scrollRef = useRef(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved != null) {
      const top = parseInt(saved, 10);
      if (!Number.isNaN(top)) el.scrollTop = top;
    }
  }, []);

  const handleSidebarScroll = () => {
    const el = scrollRef.current;
    if (el) sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop));
  };

  // (2026-05-18 A2) Mapa de refs estable por link (NO es un hook). Devuelve
  // el MISMO RefObject para cada `link.to` entre renders, así el .map() no
  // necesita useRef y la identidad (tooltip.ref === linkRef) se mantiene.
  const linkRefs = useRef({});
  const getLinkRef = (key) => {
    if (!linkRefs.current[key]) linkRefs.current[key] = React.createRef();
    return linkRefs.current[key];
  };

  // (2026-06-09) Secciones del menú colapsables. Por defecto TODAS arrancan
  // RESUMIDAS (colapsadas); el usuario decide cuáles expandir. Se recuerda
  // durante la SESIÓN (sessionStorage) para no perder la expansión al navegar
  // — el Sidebar se re-monta en cada página. Una sesión/pestaña nueva vuelve a
  // arrancar todo colapsado. `secExpand[heading] === true` => expandida.
  const SECTIONS_KEY = "qi_sidebar_sections_expanded";
  const [secExpand, setSecExpand] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SECTIONS_KEY) || "{}") || {}; }
    catch { return {}; }
  });
  const toggleSection = (heading) => {
    setSecExpand((prev) => {
      const next = { ...prev, [heading]: !prev[heading] };
      try { sessionStorage.setItem(SECTIONS_KEY, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  // Req 1 v3+v4 (2026-05-13): visibilidad del menú "Contratos de Terceros".
  //   - admin / auditor: SIEMPRE lo ven (tienen acceso global a todos los
  //     emisores; el auditor además es solo lectura — restricción del backend).
  //   - operador_admin / visor: solo si su cliente vinculado es emisor
  //     (no tiene sentido administrar terceros si el cliente no emite).
  //   - operador y demás roles: nunca.
  const esClienteEmisor = user?.es_emisor === true;
  const esRolGlobal = rol === 'admin' || rol === 'auditor';

  // ─────────────────────────────────────────────────────────────────────────
  // (2026-08-17) MÓDULO DE COMPRAS OCULTO EN EL MENÚ — decisión del usuario.
  //
  // POR QUÉ: el flujo de compras quedó FUERA del alcance de la revisión de
  // agosto, y la auditoría adversarial del 2026-08-14 midió que sigue vivo
  // allí un defecto de la «vía V3»: en `compras_service` y en
  // `compras_routes._crear_items`, los campos `producto_id`,
  // `categoria_gasto_id` y `descuento_porcentaje` viajan CRUDOS dentro del
  // dict de la línea (`{**it}`), llegan al INSERT y revientan en el COMMIT
  // con `DataError` → HTTP 500. Es preexistente, no lo introdujo la revisión.
  //
  // QUÉ HACE Y QUÉ NO: esto oculta las ENTRADAS DEL MENÚ, nada más. Las rutas
  // de React siguen registradas y los endpoints del backend siguen abiertos:
  // quien tenga la URL o llame a la API entra igual. NO es un control de
  // seguridad; es un control de visibilidad para que el operador no llegue por
  // el menú a un flujo no revisado.
  //
  // PARA REACTIVARLO: poner esta constante a `false` y reconstruir el bundle.
  // No hay más sitios que tocar.
  const COMPRAS_OCULTO_EN_MENU = true;

  const menuSections = [
    {
      heading: "Panel de Control",
      links: [
        { to: "/dashboard", icon: "fas fa-chart-line", label: "Ventas" },
        ...(COMPRAS_OCULTO_EN_MENU ? [] : [
          { to: "/dashboard/compras", icon: "fas fa-cart-shopping", label: "Compras" },
        ]),
      ],
    },
    {
      heading: "Documentos Legales",
      links: [
        // (2026-06-09) Clientes y Terceros se movieron aquí desde "Configuración".
        // Conservan sus roles/predicates exactos.
        { to: "/clients", icon: "fas fa-users", label: "Clientes", roles: ["operador_admin", "operador", "visor"] },
        { to: "/terceros", icon: "fas fa-user-friends", label: "Terceros", roles: ["operador_admin", "operador", "visor"], predicate: () => esClienteEmisor },
        { to: "/proformas", icon: "fas fa-file-import", label: "Proformas" },
        { to: "/invoices", icon: "fas fa-file-invoice", label: "Facturas" },
        { to: "/credit-note", icon: "fas fa-file-invoice", label: "Notas de Crédito" },
        { to: "/debit-note", icon: "fas fa-file-invoice", label: "Notas de Débito" },
        { to: "/withholdings", icon: "fas fa-file-invoice", label: "Retenciones" },
        { to: "/dispatch-guide", icon: "fas fa-file-invoice", label: "Guía de despacho" },
        { to: "/salesBook", icon: "fas fa-file-invoice", label: "Libro de ventas" },
      ],
    },
    {
      heading: "Configuración",
      links: [
        // (2026-05-18) El auditor ve el MISMO menú "Clientes" que el admin
        // (lista global /empresas), no el de endClients. Acceso de SOLO
        // lectura (sin crear/editar/eliminar; ver canManage en ListClients
        // y el bloqueo de escrituras de auditor en el backend).
        { to: "/empresas", icon: "fas fa-users", label: "Empresas", roles: ["admin", "auditor"] },
        // (2026-06-09) "Clientes" (/clients) y "Terceros" (/terceros) se movieron
        // a la sección "Documentos Legales" (conservan roles/predicates).
        { to: "/contracts", icon: "fas fa-file-contract", label: "Contratos de Terceros", roles: ["admin", "operador_admin", "visor", "auditor"], predicate: () => esRolGlobal || esClienteEmisor },
        { to: "/products", icon: "fas fa-cubes", label: "Producto/Servicio", roles: ["admin", "operador_admin", "operador", "visor", "auditor"] },
        { to: "/taxpayer", icon: "fas fa-tags", label: "Tipos de Contribuyentes", roles: ["admin", "operador_admin", "visor", "auditor"] },
        { to: "/taxes", icon: "fas fa-percent", label: "Impuestos", roles: ["admin", "operador_admin", "visor", "auditor"] },
        { to: "/config-withholdings", icon: "fas fa-percent", label: "Retenciones", roles: ["admin", "operador_admin", "visor", "auditor"] },
        { to: "/config-exchange-rate-history", icon: "fa-solid fa-list-ul", label: "Tasas de cambios BCV", roles: ["admin", "operador_admin", "visor", "auditor"] },
        { to: "/config-payment-methods", icon: "fas fa-tags", label: "Métodos de pago", roles: ["admin", "operador_admin", "visor", "auditor"] },
        // (2026-07-02) SENIAT Fase 1: catálogos oficiales GGTIC.GIT.00.02 v1.4.
        // SOLO visible para admin/auditor (solo visualización; la edición se
        // realiza directamente en base de datos).
        { to: "/catalogos-seniat", icon: "fas fa-book-open", label: "Catálogos SENIAT", roles: ["admin", "auditor"] },
        { to: "/auditlogs", icon: "fa-solid fa-list-ul", label: "Registro de auditoria", roles: ["admin"] },
        { to: "/company-users", icon: "fas fa-users", label: "Usuarios", roles: ["admin", "operador_admin", "visor", "auditor"] },
      ],
    },
    // (2026-05-21) Cobranza mensual — solo visible para admin.
    {
      heading: "Cobranza",
      links: [
        { to: "/billing-rules", icon: "fas fa-scale-balanced", label: "Reglas de cobro", roles: ["admin"] },
        { to: "/billing-history", icon: "fas fa-calendar-check", label: "Historico de cobranza", roles: ["admin"] },
      ],
    },
    // (2026-06-08) Módulo de Compras.
    // (2026-08-17) OCULTO por `COMPRAS_OCULTO_EN_MENU` — ver el bloque de
    // arriba. La sección entera desaparece del menú: sus 5 enlaces son el
    // flujo de compras completo (proveedores, órdenes, facturas de compra,
    // gastos y libro de compras).
    ...(COMPRAS_OCULTO_EN_MENU ? [] : [{
      heading: "Compras",
      links: [
        { to: "/compras/proveedores", icon: "fas fa-truck", label: "Proveedores", roles: ["admin", "operador_admin", "operador"] },
        { to: "/compras/ordenes", icon: "fas fa-clipboard-list", label: "Órdenes de compra", roles: ["admin", "operador_admin", "operador"] },
        { to: "/compras/facturas", icon: "fas fa-file-invoice-dollar", label: "Compras", roles: ["admin", "operador_admin", "operador"] },
        { to: "/compras/gastos", icon: "fas fa-receipt", label: "Gastos", roles: ["admin", "operador_admin", "operador"] },
        { to: "/compras/libro", icon: "fas fa-book", label: "Libro de Compras", roles: ["admin", "operador_admin", "operador", "auditor"] },
      ],
    }]),
    // (2026-05-23 v7) Documentacion publica para integradores.
    //   - admin: siempre visible (acceso global a herramientas).
    //   - operador_admin: solo si tiene es_integrador=True (cuenta tecnica
    //     destinada a integracion via API; los operadores admin humanos
    //     que NO integran no necesitan la documentacion).
    //   - visor: siempre visible — los visores tradicionales pueden consultar
    //     la documentacion para entender el sistema. Este rol tambien cubre
    //     el caso del integrador degradado a 'visor' tras login UI (v15):
    //     el predicate exigia `es_integrador=true` que SI viene en el response
    //     del login, pero el filtro `roles` rechazaba el rol 'visor' antes
    //     de llegar al predicate. Al agregar 'visor' a `roles` ambos casos
    //     (visor humano y integrador degradado) quedan cubiertos.
    //   - resto de roles (operador, auditor, superusuario): oculto.
    // El item abre la landing publica /docs/integracion/ en una NUEVA pestaña
    // (external: true) — es contenido fuera de la SPA.
    {
      heading: "Documentación",
      links: [
        {
          // (2026-05-26 v17) URL relativa: el docs/integracion/ se sirve
          // desde el mismo dominio que el SPA, así que el link abre el path
          // relativo a window.location.origin. Esto desacopla el sidebar del
          // dominio hardcodeado — al cambiar de hosting basta con que la
          // ruta /docs/integracion/ siga existiendo en el nuevo servidor.
          to: "/docs/integracion/",
          icon: "fas fa-book",
          label: "Manual + API",
          roles: ["admin", "operador_admin", "visor"],
          predicate: () => (
            rol === 'admin'
            || rol === 'visor'
            || (rol === 'operador_admin' && user?.es_integrador === true)
          ),
          external: true,
        },
      ],
    },
  ];

  // (2026-06-09) Orden de las secciones en el menú. Compras va de 2º
  // (después de Inicio) y Configuración al final. Las secciones no listadas
  // aquí conservan su orden relativo al inicio.
  const SECTION_ORDER = [
    "Panel de Control", "Documentos Legales", "Compras", "Configuración",
    "Cobranza", "Documentación",
  ];
  const orderedSections = [...menuSections].sort(
    (a, b) => {
      const ia = SECTION_ORDER.indexOf(a.heading);
      const ib = SECTION_ORDER.indexOf(b.heading);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    }
  );

  const inicial = (user?.nombre || "U").trim().charAt(0).toUpperCase();
  const rolLabel = (user?.rol || "").replace(/_/g, " ");

  return (
    <>
      {/* BOTÓN HAMBURGUESA MOBILE */}
      <button
        className="fixed top-4 left-4 z-40 md:hidden text-white shadow-sirius-lg p-2.5 rounded-xl"
        style={{ background: "linear-gradient(135deg,#1A1A1A,#1C2267)" }}
        onClick={() => setMobileOpen(!mobileOpen)}
        data-tooltip={mobileOpen ? "Cerrar menú" : "Abrir menú"}
        data-tooltip-pos="right"
        aria-label="Menú"
      >
        <i className={`fas ${mobileOpen ? "fa-times" : "fa-bars"}`} />
      </button>

      {/* OVERLAY MOBILE */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 md:hidden"
          style={{ backgroundColor: "rgba(13,13,16,0.55)", backdropFilter: "blur(4px)" }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      <nav
        className={`
          fixed top-0 left-0 h-screen z-30 py-4 overflow-hidden
          transform transition-all duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
          ${collapsed ? "md:w-20" : "md:w-72"}
          w-72
        `}
      >
        {/* Capas de fondo nocturno Sirius */}
        <div className="absolute inset-0 bg-sirius-night" />
        <div className="absolute inset-0 bg-sirius-mesh opacity-30" />
        <div className="absolute top-0 right-0 h-full w-px bg-gradient-to-b from-transparent via-spark-500/30 to-transparent" />

        <div className={`relative ${collapsed ? "px-2" : "px-5"} h-full flex flex-col`}>
          {/* LOGO */}
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="block py-2 w-full">
              <div className={`relative flex items-center justify-center h-16 rounded-xl bg-white/95 shadow-spark ${collapsed ? "px-1" : "px-3"} py-2`}>
                <img
                  src={Logo}
                  alt="Quantus Invoice"
                  className={`absolute transition-all duration-300 ${collapsed ? "opacity-0 scale-90" : "opacity-100 scale-100"}`}
                  style={{ width: 140 }}
                />
                <img
                  src={LogoCollapse}
                  alt="Quantus Invoice"
                  className={`absolute transition-all duration-300 ${collapsed ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
                  style={{ width: 32 }}
                />
              </div>
              {!collapsed && (
                <p className="text-center text-[0.62rem] tracking-[0.22em] uppercase text-white/40 mt-2 font-light-display">
                  by Sirius Solutions
                </p>
              )}
            </Link>

            {/* Botón colapsar desktop */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex items-center justify-center w-7 h-7 rounded-lg text-white/55 hover:text-white hover:bg-white/10 ml-1 transition-all"
              data-tooltip={collapsed ? "Expandir" : "Colapsar"}
              data-tooltip-pos={collapsed ? "right" : "bottom"}
              aria-label="Colapsar menú"
            >
              <i className={`fas ${collapsed ? "fa-chevron-right" : "fa-chevron-left"} text-xs`} />
            </button>
          </div>

          {/* MENÚ */}
          <div
            ref={scrollRef}
            onScroll={handleSidebarScroll}
            className="flex-1 mt-3 overflow-y-auto sidebar-scroll pr-0.5"
          >
            {orderedSections.map((section) => {
              // (2026-05-21) Calcular links visibles ANTES de renderizar la
              // seccion para evitar mostrar el heading (ej. "Cobranza")
              // cuando todos los links de la seccion estan ocultos por rol.
              const visibleLinks = section.links.filter((link) => {
                if (link.roles && !link.roles.includes(rol)) return false;
                if (typeof link.predicate === 'function' && !link.predicate()) return false;
                return true;
              });
              if (visibleLinks.length === 0) return null;
              const headingVisible = (!collapsed || mobileOpen);
              const isSecExpanded = secExpand[section.heading] === true;
              // En modo icono (sin headings) se muestran todos los links; con
              // headings visibles, solo si la sección está expandida (default: no).
              const showLinks = !headingVisible || isSecExpanded;
              return (
              <div key={section.heading}>
                <div className="my-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                {/* Título de sección — clic para expandir/colapsar (default: colapsada) */}
                {headingVisible && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.heading)}
                    className="w-full flex items-center justify-between text-white/45 hover:text-white/70 text-[0.66rem] tracking-[0.16em] uppercase font-semibold pb-3 px-1 transition-colors"
                    aria-expanded={isSecExpanded}
                  >
                    <span className="flex items-center gap-2">
                      <i className="fas fa-circle text-[5px] text-spark-400" />
                      {section.heading}
                    </span>
                    <i className={`fas fa-chevron-${isSecExpanded ? "down" : "right"} text-[0.6rem] opacity-70`} />
                  </button>
                )}
                {showLinks && (
                <ul className="flex flex-col pb-4">
                  {visibleLinks.map((link) => {
                    const active = isActive(link.to);
                    // (2026-05-18 A2) Antes: `useRef(null)` DENTRO del .map()
                    // -> hook en loop. Si cambia la cantidad de links visibles
                    // (p.ej. `es_emisor` resuelve y aparece/desaparece Terceros
                    // /Contratos) cambia el nº de hooks entre renders y React
                    // lanza "Rendered more hooks...". Se usa un mapa de refs
                    // estable (React.createRef memoizado por link.to), que NO
                    // es un hook y conserva identidad entre renders.
                    const linkRef = getLinkRef(link.to);

                    // (2026-05-23 v7) Soporte de enlaces EXTERNOS (link.external=true):
                    // para URLs fuera de la SPA (p.ej. /docs/integracion/). Se renderiza
                    // como <a> con target="_blank" y rel="noopener noreferrer" en vez
                    // de <Link> de react-router-dom (que solo maneja rutas internas).
                    const isExternal = link.external === true;
                    const linkClass = `
                            group relative flex items-center
                            ${collapsed && !mobileOpen ? "justify-center px-2" : "gap-3 pl-3 pr-9"}
                            py-2.5 text-[0.72rem] uppercase font-semibold tracking-wide
                            w-full h-11 mb-1 rounded-xl transition-all duration-200
                            ${active
                              ? "text-white bg-gradient-to-r from-spark-500/25 to-sirius-600/10"
                              : "text-white/60 hover:bg-white/5 hover:text-white"
                            }
                          `;
                    const linkInner = (
                      <>
                        {active && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-spark-400" />
                        )}
                        <span
                          className={`
                              shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-sm transition-all duration-200
                              ${active
                                ? "bg-gradient-to-br from-sirius-500 to-spark-500 text-white shadow-spark"
                                : "bg-white/5 text-white/70 group-hover:bg-white/10 group-hover:text-white"
                              }
                            `}
                        >
                          <i className={link.icon} />
                        </span>
                        {(!collapsed || mobileOpen) && <span className="truncate">{link.label}</span>}
                        {isExternal && (!collapsed || mobileOpen) && (
                          <i className="fas fa-external-link-alt ml-1 text-[0.6rem] opacity-60" />
                        )}
                      </>
                    );
                    return (
                      <li key={link.to} className="relative">
                        {isExternal ? (
                          <a
                            href={link.to}
                            target="_blank"
                            rel="noopener noreferrer"
                            ref={linkRef}
                            onMouseEnter={() => {
                              if (collapsed && !mobileOpen) {
                                setTooltip({ text: link.label, visible: true, ref: linkRef });
                              }
                            }}
                            onMouseLeave={() => setTooltip({ text: "", visible: false, ref: null })}
                            className={linkClass}
                          >
                            {linkInner}
                          </a>
                        ) : (
                        <Link
                          to={link.to}
                          ref={linkRef}
                          onMouseEnter={() => {
                            if (collapsed && !mobileOpen) {
                              setTooltip({ text: link.label, visible: true, ref: linkRef });
                            }
                          }}
                          onMouseLeave={() => setTooltip({ text: "", visible: false, ref: null })}
                          className={linkClass}
                        >
                          {linkInner}
                        </Link>
                        )}

                        {/* (2026-05-21) Icono "?" de ayuda contextual.
                            Solo en modo expandido y si la ruta tiene una
                            entrada en MENU_HELP. Es hermano del <Link> (no
                            hijo), asi que su click no dispara navegacion. */}
                        {(!collapsed || mobileOpen) && MENU_HELP[link.to] && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setHelpRuta(link.to);
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-10
                              w-5 h-5 rounded-full flex items-center justify-center
                              text-[0.6rem] text-white/45 bg-white/5
                              hover:text-white hover:bg-spark-500/50 transition-all"
                            title={`¿Qué es "${link.label}"?`}
                            aria-label={`Ayuda sobre ${link.label}`}
                          >
                            <i className="fas fa-question" />
                          </button>
                        )}

                        {/* Tooltip solo desktop colapsado */}
                        {collapsed && !mobileOpen && tooltip.ref === linkRef && (
                          <Tooltip targetRef={linkRef} text={tooltip.text} visible={tooltip.visible} />
                        )}
                      </li>
                    );
                  })}
                </ul>
                )}
              </div>
              );
            })}
          </div>

          {/* FOOTER: usuario */}
          {(!collapsed || mobileOpen) && (
            <div className="glass-panel-dark rounded-xl px-3 py-2.5 mt-2 mb-1 flex items-center gap-3">
              <span className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-spark-400 to-sirius-600 text-white flex items-center justify-center font-display font-bold text-sm">
                {inicial}
              </span>
              <div className="min-w-0">
                <p className="text-white text-xs font-semibold truncate">{user?.nombre || "Usuario"}</p>
                <p className="text-white/45 text-[0.65rem] uppercase tracking-wider truncate">{rolLabel || "—"}</p>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* (2026-05-21) Modal de ayuda contextual del menú. Se renderiza
          via portal a document.body desde el propio componente. */}
      <MenuHelpModal
        help={helpRuta ? MENU_HELP[helpRuta] : null}
        rol={rol}
        onClose={() => setHelpRuta(null)}
      />
    </>
  );
}
